/**
 * Shared utilities for Acknowledgment Workflow Modals
 *
 * Contains the useReasonCodes hook, ReasonCodeSelect component,
 * and getEffectiveReason helper function.
 */

import { useState, useEffect } from 'react';
import { purchasingAPI, ReasonCode } from '../../lib/api';

/**
 * Shared hook to fetch reason codes from the API.
 * Caches results for the component lifecycle.
 */
export function useReasonCodes() {
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    purchasingAPI.getReasonCodes({ is_active: true, page_size: 100 })
      .then(res => {
        if (!cancelled) setReasonCodes(res.data.results);
      })
      .catch(err => console.error('Failed to load reason codes:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { reasonCodes, loading };
}

/** The reason text a code submits: its description, else the code itself. */
function reasonCodeText(rc: ReasonCode): string {
  return rc.description || rc.code;
}

/**
 * Reusable reason code select dropdown with "Other (custom)" option.
 *
 * `value` stays the reason TEXT (what every caller sends as `reason`).
 * CR-8589: `onChange` also reports the picked code's id (null for the
 * placeholder or "Other"), so a change request can record which code it used
 * and Admin Setup archives that code instead of deleting it. Options are keyed
 * by id because two codes may share a description.
 */
export function ReasonCodeSelect({
  value,
  onChange,
  customValue,
  onCustomChange,
  required = false,
  label = 'Reason',
  categories,
}: {
  value: string;
  onChange: (val: string, reasonCodeId: number | null) => void;
  customValue: string;
  onCustomChange: (val: string) => void;
  required?: boolean;
  label?: string;
  /** Restrict the dropdown to specific reason-code categories (e.g.
   *  ['hold'] for the Put On Hold dialog). Omit for all categories. */
  categories?: string[];
}) {
  const { reasonCodes, loading } = useReasonCodes();
  const [pickedId, setPickedId] = useState('');

  const visibleCodes = categories?.length
    ? reasonCodes.filter((rc) => categories.includes(rc.category))
    : reasonCodes;

  // The option to show for `value`: the one picked here while its text still
  // matches, else the first code with that text (a value the caller set).
  const picked = visibleCodes.find((rc) => String(rc.id) === pickedId);
  const selectValue = value === '' || value === '__custom__'
    ? value
    : String(
      (picked && reasonCodeText(picked) === value ? picked : visibleCodes.find((rc) => reasonCodeText(rc) === value))?.id
      ?? '',
    );

  const handleChange = (next: string) => {
    const rc = visibleCodes.find((c) => String(c.id) === next);
    setPickedId(rc ? next : '');
    onChange(rc ? reasonCodeText(rc) : next, rc ? rc.id : null);
  };

  // Group by category
  const grouped = visibleCodes.reduce<Record<string, ReasonCode[]>>((acc, rc) => {
    const cat = rc.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(rc);
    return acc;
  }, {});

  const categoryLabels: Record<string, string> = {
    delay: 'Delay',
    delivery: 'Delivery',
    quality: 'Quality',
    quantity: 'Quantity',
    price: 'Pricing',
    pricing: 'Pricing',
    date_change: 'Date Change',
    technical: 'Technical',
    supplier: 'Supplier',
    customer: 'Customer',
    product: 'Product',
    documentation: 'Documentation',
    packaging: 'Packaging',
    hold: 'Hold',
    other: 'Other',
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={selectValue}
        onChange={(e) => handleChange(e.target.value)}
        required={required && value !== '__custom__'}
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">Select a reason...</option>
        {loading ? (
          <option disabled>Loading reason codes...</option>
        ) : (
          Object.entries(grouped).map(([category, codes]) => (
            <optgroup key={category} label={categoryLabels[category] || category}>
              {codes.map(rc => (
                // CR-5886 made description optional — reasonCodeText falls
                // back to the code, otherwise a description-less code
                // submitted an empty reason and rendered a dangling "CODE:"
                // label. CR-8589: the option value is the id; the text is
                // resolved in handleChange.
                <option key={rc.id} value={String(rc.id)}>
                  {rc.description ? `${rc.code}: ${rc.description}` : rc.code}
                </option>
              ))}
            </optgroup>
          ))
        )}
        <optgroup label="Custom">
          <option value="__custom__">Other (enter custom reason)</option>
        </optgroup>
      </select>
      {/* When `categories` filters the list down to nothing the dropdown would
          otherwise show only the placeholder + Custom, with no hint why. */}
      {!loading && visibleCodes.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No reason codes are configured for this change type — choose “Other” to enter one.
        </p>
      )}
      {value === '__custom__' && (
        <textarea
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
          rows={2}
          required={required}
          placeholder="Enter your reason..."
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}
    </div>
  );
}

const NO_REASON_CODE_IDS: Record<ProposedChangeLeg['field'], number | null> = { date: null, quantity: null, price: null };

/**
 * CR-8589: the reason code picked for each field of a mixed proposal.
 *
 * @returns `reasonChange(field, setText)` builds that field's ReasonCodeSelect
 *   `onChange` (keeps the text AND the id); `withReasonCodeIds(legs)` stamps
 *   each leg with its field's id; `resetReasonCodeIds` clears them after a submit.
 */
export function useReasonCodeIds() {
  const [reasonCodeIds, setReasonCodeIds] = useState(NO_REASON_CODE_IDS);
  const reasonChange = (field: ProposedChangeLeg['field'], setText: (text: string) => void) =>
    (text: string, reasonCodeId: number | null) => {
      setText(text);
      setReasonCodeIds((prev) => ({ ...prev, [field]: reasonCodeId }));
    };
  const withReasonCodeIds = (legs: ProposedChangeLeg[]): ProposedChangeLeg[] =>
    legs.map((leg) => ({ ...leg, reasonCodeId: reasonCodeIds[leg.field] }));
  return { withReasonCodeIds, reasonChange, resetReasonCodeIds: () => setReasonCodeIds(NO_REASON_CODE_IDS) };
}

/** Helper: resolve the effective reason string from select value + custom text */
export function getEffectiveReason(selectValue: string, customText: string): string {
  if (selectValue === '__custom__') return customText.trim();
  return selectValue;
}

export interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineId: number;
  lineNumber: number;
  partNumber: string;
  orderNumber?: string;
  currentDeliveryDate: string;
  // CR-4729: Supplier's proposed promise date — when present, the Accept /
  // Decline modals render "From <currentDeliveryDate> to <proposedDate>" so
  // the user clearly sees the change they're confirming, not just the
  // existing date.
  proposedDate?: string;
  // `changes` (EXPEDITING-PARITY PR 2, G12): a mixed proposal travels as ONE
  // action carrying every leg, so the server can run them in one transaction.
  onSubmit: (data: WorkflowActionPayload) => Promise<void>;
}

/**
 * What a workflow dialog asks for.
 *
 * ONE declaration: this shape was written out by hand in three places — here, in
 * `usePOLineDetail.handleWorkflowAction` and in `POLineDetailDialogs` — with
 * `action` typed as a bare `string`, so a typo compiled clean and failed at
 * runtime as a 400 nobody saw until a user pressed the button (type lens, round 2).
 * The six values are `handleWorkflowAction`'s own switch arms.
 */
export type WorkflowAction =
  | 'accept' | 'decline' | 'propose'
  | 'proposeQuantity' | 'proposePrice' | 'proposeChanges';

export interface WorkflowActionPayload {
  action: WorkflowAction;
  lineId: number;
  comments?: string;
  reason?: string;
  /** CR-8589: id of the reason code picked; null/absent for a custom reason. */
  reasonCodeId?: number | null;
  proposedDate?: string;
  proposedQuantity?: number;
  proposedPrice?: number;
  changes?: ProposedChangeLeg[];
  files?: File[];
}

/** One field of a mixed proposal (G12). */
export interface ProposedChangeLeg {
  field: 'date' | 'quantity' | 'price';
  value: string;
  reason: string;
  /** CR-8589: id of the reason code picked; null for a custom reason. */
  reasonCodeId?: number | null;
}
